// Find-screen search panel for the WM Transaction Management Data Profile (Delphi WMD0081).
//
// Every coded column is a dropdown here as well as in the grid (rule 3) — the option lists are the
// shared ones in CodeLists.js and are attached at runtime through formRefs in List.js's onFormLoad,
// which is how the dynamic form supplies a SelectBox's dataSource. `showClearButton` matters on a
// search panel: without it the dynamic form swallows the change that clears a criterion, so a filter
// once set could never be removed.
//
// Spread across four groups because the dynamic form renders one group per column; a single group
// stacks all 29 criteria into one very tall column. Same shape as the sibling data-profile screens.
//
// Only the first MAX_VISIBLE_PER_GROUP criteria of each group ship visible, so the accordion opens
// compact instead of showing all 29 at once. The rest are not removed: FormField collects every
// `visible: false` field into its Hidden Fields popup, where the user can drag any of them back into
// a group. The layout below therefore stays the full list, in priority order, and the rule is applied
// once on export rather than hand-maintained per field.
import { TABLE_NAME } from "./CodeLists";

const MAX_VISIBLE_PER_GROUP = 2;

const text = (name, label, extra = {}) => ({
    name,
    tableName: TABLE_NAME,
    label,
    type: "text",
    required: false,
    visible: true,
    isLabelChanged: false,
    ...extra,
});

const select = (name, label) => ({
    name,
    tableName: TABLE_NAME,
    label,
    type: "select",
    showClearButton: true,
    required: false,
    visible: true,
    isLabelChanged: false,
});

const date = (name, label) => ({
    name,
    tableName: TABLE_NAME,
    label,
    type: "date",
    required: false,
    visible: true,
    isLabelChanged: false,
});

const SearchLayoutDefinition = [
    {
        "groupId": 1,
        "label": "",
        "fields": [
            { ...text("RecordNumber", "Record #"), localizedField: "KEYSEQUENCE" },
            text("ProfileCode", "Profile Code", { maxLength: 10 }),
            text("Description", "Description", { maxLength: 40 }),
            text("Comment", "Comment"),
            select("Status", "Status"),
            select("TabDefaults", "Visible Tabs"),
            select("TransTabDefaults", "Default Tab"),
            select("UninvoicedChargesTab", "Un Inv Tab"),
        ],
    },
    {
        "groupId": 2,
        "label": "",
        "fields": [
            select("EdiOrdersTab", "EDI Tab"),
            select("WebOrdersTab", "Web Tab"),
            select("CustomersTab", "Customer Tab"),
            select("ItemTab", "Item Tab"),
            select("LotTab", "Lot Tab"),
            select("SubLotTab", "Sublot Tab"),
            select("LocationTab", "Location Tab"),
        ],
    },
    {
        "groupId": 3,
        "label": "",
        "fields": [
            select("TransTabUseColors", "Trans Use Color"),
            select("TransTabBasisDate", "Trans Basis Date"),
            select("TransTabPendingColor", "Trans Color Text"),
            select("TransTabWarningColor", "Trans Warning Color"),
            select("ItemTabUseColors", "Item Use Color"),
            select("ItemTabBasisQty", "Item Basis Qty"),
            select("ItemTabPendingColor", "Item Color Text"),
            select("ItemTabWarningColor", "Item Warning Color"),
        ],
    },
    {
        "groupId": 4,
        "label": "",
        "fields": [
            text("CreationUserId", "Created By"),
            date("CreationDateFrom", "Creation Date From"),
            date("CreationDateTo", "Creation Date To"),
            text("LastUpdateUserId", "Last Updated By"),
            date("LastUpdateDateFrom", "Last Updated From"),
            date("LastUpdateDateTo", "Last Updated To"),
        ],
    },
];

/**
 * Applies the per-group visibility cap. The field objects are copied rather than mutated: the
 * dynamic form treats the schema it is handed as its own and reorders it in place
 * (sortFormSchemaFieldsByVisibility), so sharing objects with the module-level definition would let
 * one mount's edits leak into the next.
 */
export const SearchLayout = SearchLayoutDefinition.map((group) => ({
    ...group,
    fields: group.fields.map((field, index) => ({
        ...field,
        visible: index < MAX_VISIBLE_PER_GROUP,
    })),
}));
